import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Bell, Clock, Wallet, CheckCircle2 } from "lucide-react";

const ICONS = { clock: Clock, wallet: Wallet, check: CheckCircle2 };

export default function NotificationsBell() {
    const [open, setOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications");
            setNotifs(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => { setOpen(o => !o); if (!open) load(); }}
                data-testid="notif-btn"
                aria-label="Notifications"
                className="relative w-9 h-9 flex items-center justify-center rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
            >
                <Bell className="w-4 h-4" />
                {notifs.length > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#00FF66] text-black text-[10px] font-bold flex items-center justify-center">
                        {notifs.length}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-slate-900 border border-slate-800 rounded-lg shadow-2xl z-50" data-testid="notif-popover">
                    <div className="p-3 border-b border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Notifications</span>
                        <span className="text-[10px] text-slate-500">{notifs.length}</span>
                    </div>
                    {loading ? (
                        <div className="p-6 text-center text-slate-500 text-sm">Loading...</div>
                    ) : notifs.length === 0 ? (
                        <div className="p-6 text-center text-slate-500 text-sm">You're all caught up.</div>
                    ) : (
                        <ul>
                            {notifs.map(n => {
                                const Icon = ICONS[n.icon] || Bell;
                                const accent = n.type === "balance" ? "text-amber-400" : n.type === "reminder" ? "text-[#00FF66]" : "text-slate-300";
                                return (
                                    <li key={n.id} className="p-3 border-b border-slate-800/60 hover:bg-slate-800/30">
                                        <div className="flex items-start gap-3">
                                            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${accent}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold text-white">{n.title}</div>
                                                <div className="text-xs text-slate-400 mt-0.5">{n.message}</div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
